import { ApiClient } from '@api/api-configuration';
import {
  Client,
  ClientCreate,
  ContactTypeEnum,
  DoctorTypeEnum,
  HomecareModelsClientsClientDoctorCreate,
} from '@api/READ_ONLY/client_api/Api';
import { Doctor } from '@api/READ_ONLY/doctors_api/Api';
import { dateOfBirthValidation } from '@app/utils/validators';
import { isValidPhone } from '@app/utils/validators/validPhone';
import useDoctors from '@hooks/fetching/useDoctors';
import { App as AntdApp, Button, DatePicker, Form, Input, Modal, Select } from 'antd';
import { Dayjs } from 'dayjs';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type QuickCreateClientModalProps = {
  open: boolean;
  onCancel: () => void;
  onCreated: (client: Client) => void;
};

type QuickCreateFormValues = Omit<ClientCreate, 'dateOfBirth'> & {
  dateOfBirth?: Dayjs;
  contactFirstName?: string;
  contactLastName?: string;
  contactPhoneMobile?: string;
  contactPhoneHome?: string;
  referringDoctorId?: number;
  attendingDoctorId?: number;
};

const QuickCreateClientModal: React.FC<QuickCreateClientModalProps> = ({ open, onCancel, onCreated }) => {
  const { t } = useTranslation();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm<QuickCreateFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [hasContactData, setHasContactData] = useState(false);

  const { doctors } = useDoctors();

  const doctorOptions = doctors.map((doctor: Doctor) => ({
    value: doctor.doctorId,
    label: `${doctor.firstName} ${doctor.lastName}`,
  }));

  // Check if any contact field has data
  const handleFormChange = useCallback(() => {
    const contactFirstName = form.getFieldValue('contactFirstName');
    const contactLastName = form.getFieldValue('contactLastName');
    const contactPhoneMobile = form.getFieldValue('contactPhoneMobile');

    const hasAnyContactData =
      (contactFirstName && contactFirstName.trim() !== '') ||
      (contactLastName && contactLastName.trim() !== '') ||
      (contactPhoneMobile && contactPhoneMobile.trim() !== '');

    setHasContactData(hasAnyContactData);
  }, [form]);

  const handleOk = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload: ClientCreate = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        city: values.city.trim(),
        phoneMobile: values.phoneMobile?.trim() || null,
        active: true,
      };

      // Add optional birthday field
      if (values.dateOfBirth) {
        const dateWithNoon = values.dateOfBirth.hour(12).minute(0).second(0).millisecond(0);
        payload.dateOfBirth = dateWithNoon.format('YYYY-MM-DD');
      }

      // Add optional social security id field
      if (values.socialSecurityId) {
        payload.socialSecurityId = values.socialSecurityId.trim();
      }

      // Add optional primary contact
      if (values.contactFirstName && values.contactLastName && values.contactPhoneMobile) {
        payload.contacts = [
          {
            firstName: values.contactFirstName?.trim(),
            lastName: values.contactLastName?.trim(),
            phoneMobile: values.contactPhoneMobile?.trim() || null,
            contactType: ContactTypeEnum.PRIMARY,
          },
        ];
      }

      // Add optional doctors using new doctors array format
      const doctors: HomecareModelsClientsClientDoctorCreate[] = [];

      if (values.referringDoctorId) {
        doctors.push({
          doctorId: values.referringDoctorId,
          doctorType: DoctorTypeEnum.REFERRING,
          startDate: new Date().toISOString().split('T')[0],
        });
      }

      if (values.attendingDoctorId) {
        doctors.push({
          doctorId: values.attendingDoctorId,
          doctorType: DoctorTypeEnum.ATTENDING,
          startDate: new Date().toISOString().split('T')[0],
        });
      }

      if (doctors.length > 0) {
        payload.doctors = doctors;
      }

      const res = await ApiClient.clientApi.clients.createClient(payload);
      message.success(t('clients-quick-create-success'));
      form.resetFields();
      onCreated(res.data);
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'errorFields' in err) {
        return;
      }
      console.error('Failed to create client', err);
      message.error(t('clients-quick-create-error'));
    } finally {
      setSubmitting(false);
    }
  }, [form, message, onCreated, t]);

  return (
    <Modal
      open={open}
      title={t('clients-quick-create-title')}
      onCancel={onCancel}
      width={700}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          {t('common-cancel')}
        </Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={handleOk}>
          {t('common-save')}
        </Button>,
      ]}
      destroyOnHidden
      maskClosable={false}
    >
      <Form form={form} layout="vertical" preserve={false} onValuesChange={handleFormChange}>
        <div className="grid grid-cols-2 gap-2">
          <Form.Item
            label={t('clients-quick-create-label-first-name')}
            name="firstName"
            rules={[{ required: true, message: t('clients-validation-first-name-required') }]}
          >
            <Input size="large" placeholder={t('clients-quick-create-placeholder-first-name')} autoComplete="off" />
          </Form.Item>

          <Form.Item
            label={t('clients-quick-create-label-last-name')}
            name="lastName"
            rules={[{ required: true, message: t('clients-validation-last-name-required') }]}
          >
            <Input size="large" placeholder={t('clients-quick-create-placeholder-last-name')} autoComplete="off" />
          </Form.Item>
        </div>

        {/* City, Phone, and Birthday in same row */}
        <div className="grid grid-cols-2 gap-2">
          <Form.Item
            label={t('clients-quick-create-label-city')}
            name="city"
            rules={[{ required: true, message: t('clients-validation-city-required') }]}
          >
            <Input size="large" placeholder={t('clients-quick-create-placeholder-city')} autoComplete="off" />
          </Form.Item>

          <Form.Item
            label={t('clients-quick-create-label-mobile-phone')}
            name="phoneMobile"
            rules={[
              { required: true, message: t('clients-validation-phone-required') },
              {
                validator: (_: unknown, value: string) =>
                  !value || isValidPhone(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('clients-validation-phone-invalid'))),
              },
            ]}
          >
            <Input placeholder={t('clients-quick-create-placeholder-mobile-phone')} autoComplete="off" size="large" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Form.Item
            label={t('clients-quick-create-label-birthday')}
            name="dateOfBirth"
            rules={[
              {
                validator: (_: unknown, value: Dayjs) => {
                  if (!value) return Promise.resolve();

                  // Convert Dayjs to ISO string format for the validation function
                  const isoString = value.format('YYYY-MM-DD');
                  const validationResult = dateOfBirthValidation.validate(isoString);

                  if (validationResult === true) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(validationResult as string));
                },
              },
            ]}
          >
            <DatePicker
              size="large"
              placeholder={t('clients-quick-create-placeholder-birthday')}
              className="w-full"
              format="YYYY-MM-DD"
              onChange={(date) => {
                if (date) {
                  const dateWithNoon = date.hour(12).minute(0).second(0).millisecond(0);
                  form.setFieldValue('dateOfBirth', dateWithNoon);
                }
              }}
            />
          </Form.Item>

          <Form.Item
            label={t('clients-quick-create-label-social-security')}
            name="socialSecurityId"
            // TODO : add validation for social security id ? exact length ? format ?
          >
            <Input
              size="large"
              placeholder={t('clients-quick-create-placeholder-social-security')}
              autoComplete="off"
            />
          </Form.Item>
        </div>

        {/* Optional Primary Contact Fields */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            {t('clients-quick-create-label-primary-contact')}{' '}
            {hasContactData && (
              <span className="text-xs text-gray-500">{t('clients-quick-create-label-all-required')}</span>
            )}
          </h4>

          <div className="grid grid-cols-3 gap-2">
            <Form.Item
              label={t('clients-quick-create-label-contact-first-name')}
              name="contactFirstName"
              rules={[
                {
                  validator: (_: unknown, value: string) => {
                    if (hasContactData && (!value || value.trim() === '')) {
                      return Promise.reject(new Error(t('clients-validation-contact-first-name-required')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                size="large"
                placeholder={t('clients-quick-create-placeholder-contact-first-name')}
                autoComplete="off"
              />
            </Form.Item>

            <Form.Item
              label={t('clients-quick-create-label-contact-last-name')}
              name="contactLastName"
              rules={[
                {
                  validator: (_: unknown, value: string) => {
                    if (hasContactData && (!value || value.trim() === '')) {
                      return Promise.reject(new Error(t('clients-validation-contact-last-name-required')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                size="large"
                placeholder={t('clients-quick-create-placeholder-contact-last-name')}
                autoComplete="off"
              />
            </Form.Item>

            <Form.Item
              label={t('clients-quick-create-label-contact-mobile-phone')}
              name="contactPhoneMobile"
              rules={[
                {
                  validator: (_: unknown, value: string) => {
                    if (hasContactData && (!value || value.trim() === '')) {
                      return Promise.reject(new Error(t('clients-validation-contact-phone-required')));
                    }
                    if (value && !isValidPhone(value)) {
                      return Promise.reject(new Error(t('clients-validation-phone-invalid')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                size="large"
                placeholder={t('clients-quick-create-placeholder-contact-mobile-phone')}
                autoComplete="off"
              />
            </Form.Item>
          </div>

          {/* Doctors */}
          <div className="grid grid-cols-2 gap-2">
            <Form.Item label={t('clients-quick-create-label-referring-doctor')} name="referringDoctorId">
              <Select
                options={doctorOptions}
                size="large"
                placeholder={t('clients-quick-create-placeholder-referring-doctor')}
                allowClear
              />
            </Form.Item>

            <Form.Item label={t('clients-quick-create-label-attending-doctor')} name="attendingDoctorId">
              <Select
                options={doctorOptions}
                size="large"
                placeholder={t('clients-quick-create-placeholder-attending-doctor')}
                allowClear
              />
            </Form.Item>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default QuickCreateClientModal;
